// src/utils/diarioOre.js
// ═══════════════════════════════════════════════════════════════════════════
// LE MIE ORE — diario delle ore per commessa (ottobre 2026, W402)
// ═══════════════════════════════════════════════════════════════════════════
// Per chi nella stessa giornata lavora su più commesse (tipico dei tecnici d'ufficio e
// degli studi tecnici): due ore su una, un'ora su un'altra, poi di nuovo la prima.
// Il cartellino dice QUANTE ore si è lavorato (presenze e paghe); il diario dice SU QUALI
// commesse. Serve a sapere quanto costa ogni commessa, non a controllare le persone.
//
// Ogni voce del diario (raccolta «diarioOre») è un blocco di tempo:
//   { orgId, uid, nomeUtente, data: "aaaa-mm-gg",
//     projectId ("" = Ufficio / generale), projectNome, codiceCommessa,
//     inizio (ms) | null, fine (ms) | null, minuti (solo voci a mano «2 h su X») | null,
//     inCorso: true/false (il cronometro sta girando su questo blocco), nota,
//     origine: "cronometro" | "manuale", createdAt, updatedAt }
// Il cronometro salva l'ORA DI INIZIO, non un contatore: chiudendo l'app o spegnendo il
// telefono il tempo continua a contare e riaprendo lo si ritrova.
//
// Chi usa il diario (W434): le persone con la scheda in Dipendenti collegata al proprio
// account e «Categoria operativa» = Non operativo (PM, ufficio, direzione). Per loro la
// pillola c'è sempre e le ore vanno sempre nei costi delle commesse, al Costo/h della
// scheda: nessuna impostazione da ricordare. Gli Operativi la pillola non la vedono: le loro
// ore arrivano dalla fonte dell'azienda (programmazione, rapportini o presenze), altrimenti
// le stesse ore conterebbero due volte.
// Qui solo funzioni pure, provate a parte.
// ═══════════════════════════════════════════════════════════════════════════

import { segmentiGiornata } from "./segmentiGiornata";

/** Valore di «Categoria operativa» in Dipendenti per chi usa il diario. */
export const NON_OPERATIVO = "Non operativo";

/** Le ore sulle commesse di questa persona arrivano da «Le mie ore»? Solo Non operativi con
 *  account collegato (la pillola registra l'account, il costo sta nella scheda). */
export const usaDiario = (dipendente) => !!dipendente?.nexoraUid && dipendente.ruolo === NON_OPERATIVO;

export const UFFICIO = { id: "", nome: "Ufficio / generale", codice: "" };

const p2 = (n) => String(n).padStart(2, "0");

/** "aaaa-mm-gg" del giorno (ora locale) di un istante in millisecondi. */
export function giornoDi(ms) {
  const d = new Date(ms);
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
}

/** Istante (ms) di un orario "HH:MM" in un giorno "aaaa-mm-gg" (ora locale). */
export function istante(giorno, hhmm) {
  const [a, m, g] = String(giorno).split("-").map(Number);
  const [h, mi] = String(hhmm || "0:0").split(":").map(Number);
  return new Date(a, (m || 1) - 1, g || 1, h || 0, mi || 0, 0, 0).getTime();
}

/** "09:05" */
export const oraDi = (ms) => { const d = new Date(ms); return `${p2(d.getHours())}:${p2(d.getMinutes())}`; };

/** Durata del blocco in minuti (il blocco in corso conta fino ad adesso). */
export function minutiBlocco(b, adesso = Date.now()) {
  if (!b) return 0;
  if (Number.isFinite(b.minuti) && b.minuti > 0 && !b.inizio) return Math.round(b.minuti);
  if (!b.inizio) return 0;
  const fine = b.inCorso ? adesso : (b.fine || b.inizio);
  return Math.max(0, Math.round((fine - b.inizio) / 60000));
}

/** "3 h 10" · "45 min" · "2 h" */
export function durata(min) {
  const m = Math.max(0, Math.round(min || 0));
  const h = Math.floor(m / 60), r = m % 60;
  if (!h) return `${r} min`;
  return r ? `${h} h ${p2(r)}` : `${h} h`;
}

/** Il cronometro: "1:23:45" */
export function cronometro(msTrascorsi) {
  const s = Math.max(0, Math.floor(msTrascorsi / 1000));
  return `${Math.floor(s / 3600)}:${p2(Math.floor((s % 3600) / 60))}:${p2(s % 60)}`;
}

/**
 * La giornata: blocchi in ordine di orario (prima quelli con orario, poi quelli a mano),
 * totale e minuti per commessa (dalla più lunga).
 */
export function riepilogoGiornata(blocchi = [], adesso = Date.now()) {
  const ordinati = [...blocchi].sort((a, b) => (a.inizio || Infinity) - (b.inizio || Infinity) || String(a.id).localeCompare(String(b.id)));
  const per = new Map();
  let totale = 0;
  ordinati.forEach(b => {
    const min = minutiBlocco(b, adesso);
    totale += min;
    const k = b.projectId || "";
    const x = per.get(k) || { projectId: k, nome: k ? (b.projectNome || "Commessa") : UFFICIO.nome, codice: k ? (b.codiceCommessa || "") : "", minuti: 0 };
    x.minuti += min; per.set(k, x);
  });
  const perCommessa = [...per.values()].filter(x => x.minuti > 0).sort((a, b) => b.minuti - a.minuti);
  const sulleCommesse = perCommessa.filter(x => x.projectId).reduce((t, x) => t + x.minuti, 0);
  return { blocchi: ordinati, totale, sulleCommesse, perCommessa };
}

/**
 * Commesse da proporre come pulsanti: prima quelle usate di recente (ultime prima), poi
 * quelle di cui si è responsabili, poi le altre commesse attive più recenti. Senza doppioni.
 */
export function commesseProposte(progetti = [], { recenti = [], uid = "", max = 8 } = {}) {
  const attive = progetti.filter(p => p && p.id && !p.archived && !p.isParent && p.statoCommessa !== "chiusa");
  const perId = new Map(attive.map(p => [p.id, p]));
  const scelte = [];
  const metti = (p) => { if (p && !scelte.includes(p) && scelte.length < max) scelte.push(p); };
  recenti.forEach(id => metti(perId.get(id)));
  attive.filter(p => uid && p.responsabileUid === uid).forEach(metti);
  [...attive].sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)).forEach(metti);
  return scelte;
}

/** Ricerca nelle commesse attive per numero, nome o cliente. */
export function cercaCommesse(progetti = [], testo = "") {
  const q = String(testo).trim().toLowerCase();
  const attive = progetti.filter(p => p && p.id && !p.archived && !p.isParent && p.statoCommessa !== "chiusa");
  if (!q) return attive.slice(0, 30);
  return attive.filter(p => [p.codiceCommessa, p.name, p.clienteNome].some(v => String(v || "").toLowerCase().includes(q))).slice(0, 30);
}

/** Aggiorna l'elenco delle commesse usate di recente (la più recente prima, al massimo 12). */
export const aggiornaRecenti = (recenti = [], projectId) =>
  projectId ? [projectId, ...recenti.filter(x => x !== projectId)].slice(0, 12) : recenti;

/**
 * Controlli di un blocco modificato a mano. Restituisce un messaggio d'errore o "".
 * Un blocco con orario deve finire dopo l'inizio, nello stesso giorno, e non nel futuro.
 */
export function erroreBlocco({ inizio, fine, minuti }, adesso = Date.now()) {
  if (inizio == null && fine == null) return minuti > 0 ? (minuti > 24 * 60 ? "Non più di 24 ore." : "") : "Indica la durata.";
  if (!inizio || !fine) return "Indica l'ora di inizio e di fine.";
  if (fine <= inizio) return "L'ora di fine deve venire dopo l'ora di inizio.";
  if (giornoDi(inizio) !== giornoDi(fine - 1)) return "Il blocco deve stare nello stesso giorno.";
  if (fine > adesso + 60000) return "L'ora di fine non può essere nel futuro.";
  return "";
}

/** Blocchi che si sovrappongono a quello indicato (per un avviso, non un blocco). */
export function sovrapposti(blocco, altri = [], adesso = Date.now()) {
  if (!blocco?.inizio) return [];
  const f = blocco.inCorso ? adesso : blocco.fine;
  return altri.filter(b => b.id !== blocco.id && b.inizio && b.inizio < f && (b.inCorso ? adesso : b.fine) > blocco.inizio);
}

// ═══════════════════════════════════════════════════════════════════════════
// INGRESSO NEI COSTI DELLE COMMESSE (W403)
// ═══════════════════════════════════════════════════════════════════════════
// Per le persone «dal diario» le ore sulle commesse vengono SOLO dal diario: le loro ore da
// programmazione e rapportini restano sui documenti ma non diventano costo (mai le stesse
// ore due volte). Con fonte Presenze il cartellino di queste persone non crea costi sulle
// commesse (utils/contabilizzaPresenze.js e il cartellino). Il tempo in corso conta fino a
// adesso, come i noleggi aperti.

/** Gli account (uid) delle persone le cui ore sulle commesse arrivano dal diario. */
export const uidConDiario = (dipendenti = []) =>
  new Set(dipendenti.filter(d => d && usaDiario(d)).map(d => d.nexoraUid));

/** Le ore del diario come voci della fonte ore: { chiave: uid, uid, nome, data, ore, rif }.
 *  Un tempo rimasto acceso da un giorno precedente non conta finché non viene chiuso, come nei
 *  costi (W415: prima in Cabina di Regia e nel Report continuava a crescere). */
export function oreDaDiario(blocchi = [], adesso = Date.now(), nomi = {}) {
  return blocchi.filter(b => b && b.projectId && !dimenticato(b, adesso)).map(b => ({
    chiave: b.uid, uid: b.uid, nome: nomi[b.uid] || b.nomeUtente || "Collaboratore", data: b.data || "",
    ore: Math.round(minutiBlocco(b, adesso) / 60 * 100) / 100, rif: "Le mie ore",
  })).filter(v => v.ore > 0);
}

/** Toglie le voci delle persone «dal diario» (la loro fonte sono le ore del diario). */
export const senzaPersoneDiario = (voci = [], uidDiario = new Set()) =>
  uidDiario.size ? voci.filter(v => !uidDiario.has(v.uid || v.chiave) && !uidDiario.has(v.nexoraUid) && !uidDiario.has(v.uidCella)) : voci; // uidCella: programmazione (W453), come il Controllo Economico

/** Costo del diario per persona: [{ uid, nome, ore, costoOrario, totale }] con il costo orario dei Dipendenti. */
/** Tempo rimasto acceso da un giorno precedente: non si conta finché non viene chiuso (W412). */
export const dimenticato = (b, adesso = Date.now()) => !!(b?.inCorso && b.data && b.data < giornoDi(adesso));

export function costoDiario(blocchi = [], dipendenti = [], adesso = Date.now(), num = (v) => Number(String(v ?? "").replace(",", ".")) || 0) {
  const per = new Map();
  // un tempo rimasto acceso da ieri crescerebbe all'infinito nei costi: resta fuori finché
  // la persona non scrive a che ora ha finito (lo segnala la contabilità di «Le mie ore»)
  blocchi.filter(b => b && b.projectId && !dimenticato(b, adesso)).forEach(b => {
    const dip = dipendenti.find(d => d.nexoraUid === b.uid);
    const x = per.get(b.uid) || { uid: b.uid, nome: dip ? `${dip.nome || ""} ${dip.cognome || ""}`.trim() : (b.nomeUtente || "Collaboratore"),
      ore: 0, costoOrario: num(dip?.costoOrario) || 0, totale: 0 };
    x.ore += minutiBlocco(b, adesso) / 60;
    per.set(b.uid, x);
  });
  return [...per.values()].map(x => ({ ...x, ore: Math.round(x.ore * 100) / 100, totale: Math.round(x.ore * x.costoOrario * 100) / 100 })).filter(x => x.ore > 0);
}

// ── Riscontro con il cartellino (W403) ───────────────────────────────────────
// Solo per la persona stessa e solo come aiuto: «oggi hai segnato 6 h 10 su 8 h di
// presenza». Minuti lavorati del cartellino: stesso calcolo del cartellino (pausa di un'ora
// dalle 6 ore in su), senza Ferie/Permesso/Malattia.
export function minutiCartellino(timbrature = []) {
  if (!timbrature.length) return 0;
  const lordi = segmentiGiornata(timbrature).segmenti.filter(s => !s.speciale).reduce((t, s) => t + (s.minuti || 0), 0);
  return lordi >= 360 ? Math.max(0, lordi - 60) : lordi;
}
/** Id del cartellino del mese di un giorno "aaaa-mm-gg" (come in Presenze: mese senza zero). */
export const idCartellino = (orgId, uid, giorno) => `${orgId}_${uid}_${Number(giorno.slice(0, 4))}_${Number(giorno.slice(5, 7))}`;
/** Messaggio del riscontro, o "" se non serve (differenza sotto i 15 minuti o niente da confrontare). */
export function riscontroCartellino(minutiDiario, minutiCart) {
  if (!(minutiCart > 0) || !(minutiDiario >= 0)) return "";
  const diff = minutiCart - minutiDiario;
  if (Math.abs(diff) < 15) return "";
  return diff > 0
    ? `Hai segnato ${durata(minutiDiario)} su ${durata(minutiCart)} di presenza: mancano ${durata(diff)}. Vuoi completare?`
    : `Hai segnato ${durata(minutiDiario)}, più delle ${durata(minutiCart)} di presenza sul cartellino: controlla se c'è un blocco di troppo.`;
}

// ── Contabilità di «Le mie ore» nel Controllo Economico (W412) ───────────────
/**
 * Dettaglio per la commessa: chi è contabilizzato (persone «Da Le mie ore»), con giorni e
 * blocchi, e chi segna in «Le mie ore» ma per i costi usa la fonte dell'azienda (solo
 * informativo). I totali coincidono con costoDiario (stessi arrotondamenti).
 * → { persone: [{ uid, nome, costoOrario, minuti, ore, totale, giorni: [{ data, minuti, blocchi }] }],
 *     fuori: [{ uid, nome, minuti }], dimenticati: [blocco], inCorso, minuti, totale, mesi: [{ mese, minuti, totale }] }
 */
export function contabilitaDiario(blocchi = [], dipendenti = [], uidContati = new Set(), adesso = Date.now(), num) {
  const tutti = (blocchi || []).filter(b => b && b.projectId);
  const dim = tutti.filter(b => dimenticato(b, adesso));
  const validi = tutti.filter(b => !dimenticato(b, adesso));
  const contati = validi.filter(b => uidContati.has(b.uid));
  const costi = costoDiario(contati, dipendenti, adesso, num);
  const nomeDi = (uid, b) => { const d = dipendenti.find(x => x.nexoraUid === uid); return d ? `${d.nome || ""} ${d.cognome || ""}`.trim() : (b?.nomeUtente || "Collaboratore"); };
  const ordina = (a, b) => (a.data || "").localeCompare(b.data || "") || (a.inizio || Infinity) - (b.inizio || Infinity); // nel giorno: per orario, «a mano» in fondo
  const persone = costi.map(c => {
    const suoi = contati.filter(b => b.uid === c.uid).sort(ordina);
    const giorni = [];
    suoi.forEach(b => {
      let g = giorni.find(x => x.data === b.data);
      if (!g) { g = { data: b.data, minuti: 0, blocchi: [] }; giorni.push(g); }
      const m = minutiBlocco(b, adesso);
      g.minuti += m; g.blocchi.push({ ...b, minutiCalcolati: m });
    });
    return { ...c, minuti: suoi.reduce((t, b) => t + minutiBlocco(b, adesso), 0), senzaCosto: !(c.costoOrario > 0), giorni: giorni.reverse() };
  }).sort((a, b) => b.totale - a.totale || b.minuti - a.minuti);
  const fuoriMappa = new Map();
  validi.filter(b => !uidContati.has(b.uid)).forEach(b => {
    const x = fuoriMappa.get(b.uid) || { uid: b.uid, nome: nomeDi(b.uid, b), minuti: 0 };
    x.minuti += minutiBlocco(b, adesso); fuoriMappa.set(b.uid, x);
  });
  const mesiMappa = new Map();
  persone.forEach(p => p.giorni.forEach(g => {
    const k = (g.data || "").slice(0, 7);
    const x = mesiMappa.get(k) || { mese: k, minuti: 0, totale: 0 };
    x.minuti += g.minuti; x.totale += (g.minuti / 60) * (p.costoOrario || 0); mesiMappa.set(k, x);
  }));
  return {
    persone,
    fuori: [...fuoriMappa.values()].filter(x => x.minuti > 0).sort((a, b) => b.minuti - a.minuti),
    dimenticati: dim.filter(b => uidContati.has(b.uid)),
    inCorso: contati.filter(b => b.inCorso).length,
    minuti: persone.reduce((t, p) => t + p.minuti, 0),
    totale: Math.round(persone.reduce((t, p) => t + p.totale, 0) * 100) / 100,
    mesi: [...mesiMappa.values()].map(m => ({ ...m, totale: Math.round(m.totale * 100) / 100 })).sort((a, b) => b.mese.localeCompare(a.mese)),
  };
}
